import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Plus, Edit, Trash2, Trees, Home, Heart } from 'lucide-react'

interface SocialProject {
  id: string
  name: string
  description: string | null
  project_type: 'trees' | 'family_help'
  icon_url: string | null
  external_link: string | null
  image_url: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export const SocialProjectsModule = () => {
  const { toast } = useToast()
  const [projects, setProjects] = useState<SocialProject[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingProject, setEditingProject] = useState<SocialProject | null>(null)

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_type: 'trees' as 'trees' | 'family_help',
    external_link: '',
    image_url: '',
    is_active: true
  })

  useEffect(() => {
    fetchProjects()
  }, [])

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('social_projects')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setProjects(data as SocialProject[] || [])
    } catch (error) {
      console.error('Error fetching social projects:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Projekte konnten nicht geladen werden.'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCreateProject = async () => {
    try {
      const { error } = await supabase
        .from('social_projects')
        .insert([formData])

      if (error) throw error

      toast({
        title: 'Projekt erstellt',
        description: 'Das soziale Projekt wurde erfolgreich erstellt.'
      })

      setShowCreateDialog(false)
      resetForm()
      fetchProjects()
    } catch (error) {
      console.error('Error creating project:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Projekt konnte nicht erstellt werden.'
      })
    }
  }

  const handleUpdateProject = async () => {
    if (!editingProject) return

    try {
      const { error } = await supabase
        .from('social_projects')
        .update(formData)
        .eq('id', editingProject.id)

      if (error) throw error

      toast({
        title: 'Projekt aktualisiert',
        description: 'Das soziale Projekt wurde erfolgreich aktualisiert.'
      })

      setEditingProject(null)
      resetForm()
      fetchProjects()
    } catch (error) {
      console.error('Error updating project:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Projekt konnte nicht aktualisiert werden.'
      })
    }
  }

  const handleDeleteProject = async (projectId: string) => {
    if (!confirm('Sind Sie sicher, dass Sie dieses Projekt löschen möchten?')) return

    try {
      const { error } = await supabase
        .from('social_projects')
        .delete()
        .eq('id', projectId)

      if (error) throw error

      toast({
        title: 'Projekt gelöscht',
        description: 'Das soziale Projekt wurde erfolgreich gelöscht.'
      })

      fetchProjects()
    } catch (error) {
      console.error('Error deleting project:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Projekt konnte nicht gelöscht werden.'
      })
    }
  }

  const handleEditProject = (project: SocialProject) => {
    setEditingProject(project)
    setFormData({
      name: project.name,
      description: project.description || '',
      project_type: project.project_type,
      external_link: project.external_link || '',
      image_url: project.image_url || '',
      is_active: project.is_active
    })
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      project_type: 'trees',
      external_link: '',
      image_url: '',
      is_active: true
    })
  }

  const getProjectIcon = (type: string) => {
    switch (type) {
      case 'trees': return <Trees className="h-4 w-4" />
      case 'family_help': return <Heart className="h-4 w-4" />
      default: return <Heart className="h-4 w-4" />
    }
  }

  const getProjectTypeName = (type: string) => {
    switch (type) {
      case 'trees': return 'Baumpflanzaktion'
      case 'family_help': return 'Familienhilfe'
      default: return type
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Laden...</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Soziale Projekte</CardTitle>
        <CardDescription>
          Verwalten Sie die verfügbaren sozialen Projekte für alle Unternehmen
        </CardDescription>
        <div className="flex justify-end">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Neues Projekt
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Neues soziales Projekt</DialogTitle>
                <DialogDescription>
                  Erstellen Sie ein neues soziales Projekt, das Unternehmen aktivieren können
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Projektname</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="z.B. Baumpflanzaktion"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Beschreibung</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Beschreibung des sozialen Projekts..."
                  />
                </div>
                <div>
                  <Label htmlFor="project_type">Projekttyp</Label>
                  <Select
                    value={formData.project_type}
                    onValueChange={(value: 'trees' | 'family_help') => 
                      setFormData({ ...formData, project_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trees">Baumpflanzaktion</SelectItem>
                      <SelectItem value="family_help">Familienhilfe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="external_link">Externer Link</Label>
                  <Input
                    id="external_link"
                    value={formData.external_link}
                    onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                    placeholder="https://beispiel.de"
                  />
                </div>
                <div>
                  <Label htmlFor="image_url">Bild URL</Label>
                  <Input
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://beispiel.de/bild.jpg"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Aktiv</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Abbrechen
                  </Button>
                  <Button onClick={handleCreateProject}>
                    Projekt erstellen
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {projects.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Noch keine sozialen Projekte erstellt
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {projects.map((project) => (
              <Card key={project.id} className="relative">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {getProjectIcon(project.project_type)}
                      <CardTitle className="text-lg">{project.name}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={project.is_active ? 'default' : 'secondary'}>
                        {project.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                      <Badge variant="outline">
                        {getProjectTypeName(project.project_type)}
                      </Badge>
                    </div>
                  </div>
                  {project.description && (
                    <CardDescription>{project.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditProject(project)}
                    >
                      <Edit className="mr-1 h-3 w-3" />
                      Bearbeiten
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteProject(project.id)}
                    >
                      <Trash2 className="mr-1 h-3 w-3" />
                      Löschen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editingProject} onOpenChange={(open) => !open && setEditingProject(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Projekt bearbeiten</DialogTitle>
              <DialogDescription>
                Bearbeiten Sie die Details des sozialen Projekts
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Projektname</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="z.B. Baumpflanzaktion"
                />
              </div>
              <div>
                <Label htmlFor="edit-description">Beschreibung</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Beschreibung des sozialen Projekts..."
                />
              </div>
              <div>
                <Label htmlFor="edit-project_type">Projekttyp</Label>
                <Select
                  value={formData.project_type}
                  onValueChange={(value: 'trees' | 'family_help') => 
                    setFormData({ ...formData, project_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trees">Baumpflanzaktion</SelectItem>
                    <SelectItem value="family_help">Familienhilfe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-external_link">Externer Link</Label>
                <Input
                  id="edit-external_link"
                  value={formData.external_link}
                  onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                  placeholder="https://beispiel.de"
                />
              </div>
              <div>
                <Label htmlFor="edit-image_url">Bild URL</Label>
                <Input
                  id="edit-image_url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://beispiel.de/bild.jpg"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="edit-is_active">Aktiv</Label>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingProject(null)}>
                  Abbrechen
                </Button>
                <Button onClick={handleUpdateProject}>
                  Änderungen speichern
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}